/**
 * Reading the UK Bar half of a parse result.
 *
 * `parsedJson` reaches the UI as `unknown` — it is whatever the PHP parser wrote, stored verbatim —
 * so every field below is narrowed at runtime. No casts: a value that is not the shape we expect is
 * treated as absent, never coerced. The jobs list and the job-detail panel both read through this
 * module so the two can never disagree about what a payload says.
 */

// Wording for the bar-review reason codes the parser emits. Unknown codes fall
// back to the raw code so a newly added reason is still readable, never blank.
export const BAR_REVIEW_REASON_LABELS: Record<string, string> = {
  boxes_unreadable: "Boxes the reader could not read",
  boxes_glued: "Boxes glued together",
  contacts_unreadable: "Interview contacts table unreadable",
  no_nominees: "No nominee boxes found",
  no_highlights: "No work highlights found",
  head_missing: "Chambers head fields missing",
  nominee_unnamed: "Nominee boxes with no name",
  call_year_lost: "Year of call label missing",
  silk_conflict: "Conflicting silk year",
  highlight_unowned: "Highlights with no barrister name",
  highlights_guessed: "Highlights attributed by guess",
  highlights_orphaned: "Highlights attributed to nobody",
};

export function barReviewReasonLabel(code: string): string {
  return BAR_REVIEW_REASON_LABELS[code] ?? code;
}

// Wording for the template identifiers the parser records. Today it emits exactly two; an
// identifier added later falls back to its raw value, as the reason codes do, so a new template
// shows up as itself instead of as a blank cell.
export const BAR_TEMPLATE_LABELS: Record<string, string> = {
  uk_bar_2027: "UK Bar 2027",
  bar_family_unknown: "Bar (other)",
};

export function barTemplateLabel(template: string): string {
  return BAR_TEMPLATE_LABELS[template] ?? template;
}

/**
 * The attention reasons that are drawn as their own chip in the Review column. The parser's
 * verdict already has a chip (the tier badge) and "failed" is the Status column, so only the two
 * reasons nothing else on the row shows get one. Order follows the API's fixed order.
 */
export const ATTENTION_CHIP_LABELS: Record<string, string> = {
  empty: "Empty output",
  no_template: "No template",
};

// Spelled out in `title`, like the tier chip names its level: the visible chip is two words.
const ATTENTION_CHIP_DESCRIPTIONS: Record<string, string> = {
  empty: "Empty output — the parser produced output but extracted nothing from this document",
  no_template: "No template — a Bar submission the parser did not recognise as a Bar template",
};

export function attentionChipLabel(reason: string): string {
  return ATTENTION_CHIP_LABELS[reason] ?? reason;
}

export function attentionChipDescription(reason: string): string {
  return ATTENTION_CHIP_DESCRIPTIONS[reason] ?? reason;
}

/** The reasons on a row that get a chip, in the order the API listed them. */
export function attentionChipReasons(reasons: readonly string[] | undefined): string[] {
  return (reasons ?? []).filter(reason => reason in ATTENTION_CHIP_LABELS);
}

/**
 * The Attention filter as the URL carries it. `barReview=true` is the pre-existing spelling of
 * attention=verdict and keeps working: an old bookmark lands on the same rows, and the select
 * shows "Review verdict" for it. An explicit `attention` wins when both are present.
 */
export function readAttentionFromSearchParams(params: URLSearchParams): string {
  const attention = params.get("attention");
  if (attention) return attention;
  return params.get("barReview") === "true" ? "verdict" : "";
}

export type BarReviewReason = {
  code: string;
  label: string;
  count: number;
};

/** Reason codes as readable rows, loudest first, so every surface orders them the same way. */
export function toBarReviewReasons(reasons: Record<string, number> | null): BarReviewReason[] {
  return Object.entries(reasons ?? {})
    .map(([code, count]) => ({ code, label: barReviewReasonLabel(code), count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

export function buildBarReviewTooltip(reasons: Record<string, number> | null): string {
  const entries = toBarReviewReasons(reasons);
  if (entries.length === 0) return "Flagged for review, no reason details recorded.";
  return entries.map(({ label, count }) => `${label}: ${count}`).join("\n");
}

/**
 * How loudly a flagged parse is marked, not what may be done with it: nothing anywhere gates on
 * this value. Both levels mean the same thing — a human should look at the output — and differ
 * only in how likely it is that something is actually wrong.
 */
export type BarReviewTier = "major" | "minor";

/**
 * True for the two tiers this UI knows how to draw, and only those.
 *
 * Takes `unknown` on purpose: the callers hold values from an API response, and one of them may be
 * a row that predates the rename, still carrying the earlier wording. Such a value fails here and
 * the surface falls back to its no-verdict placeholder rather than throwing or drawing a blank chip.
 */
export function isBarReviewTier(value: unknown): value is BarReviewTier {
  return value === "major" || value === "minor";
}

export type BarReviewVerdict = {
  tier: BarReviewTier;
  reasons: BarReviewReason[];
};

export type BarBarrister = {
  name: string;
  callYear: string;
  silkYear: string;
  workHighlights: number;
  workHighlightsUncertain: number | null;
  boxes: number;
  docPosition: string;
};

export type BarUnattributedHighlight = {
  name: string;
  workHighlights: number;
  docPositions: string[];
  assignedTo: string;
  uncertain: boolean;
};

export type BarView = {
  /** True when the payload came through the PHP Bar gate — a non-Bar template carries none of this. */
  hasBar: boolean;
  barristers: BarBarrister[];
  /** `null` when the payload has no such key at all; `[]` means every highlight found its nominee. */
  unattributedHighlights: BarUnattributedHighlight[] | null;
  /** Every parse writes a log; `null` only when the payload is missing or malformed. */
  log: Record<string, unknown> | null;
};

/** The one gate between `unknown` and a keyed read. Exported so the list columns narrow the same way. */
export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readString(source: Record<string, unknown>, key: string): string {
  const value = source[key];
  return typeof value === "string" ? value : "";
}

function readNumber(source: Record<string, unknown>, key: string): number {
  const value = source[key];
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function readOptionalNumber(source: Record<string, unknown>, key: string): number | null {
  const value = source[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function readStringArray(source: Record<string, unknown>, key: string): string[] {
  const value = source[key];
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is string => typeof entry === "string");
}

function readRecordOfNumbers(value: unknown): Record<string, number> | null {
  if (!isRecord(value)) return null;
  const out: Record<string, number> = {};
  for (const [key, entry] of Object.entries(value)) {
    if (typeof entry === "number" && Number.isFinite(entry)) out[key] = entry;
  }
  return out;
}

function readBarrister(entry: unknown): BarBarrister | null {
  if (!isRecord(entry)) return null;
  return {
    name: readString(entry, "name"),
    callYear: readString(entry, "call_year"),
    silkYear: readString(entry, "silk_year"),
    workHighlights: readNumber(entry, "work_highlights"),
    workHighlightsUncertain: readOptionalNumber(entry, "work_highlights_uncertain"),
    boxes: readNumber(entry, "boxes"),
    docPosition: readString(entry, "Doc_Position"),
  };
}

function readUnattributedHighlight(entry: unknown): BarUnattributedHighlight | null {
  if (!isRecord(entry)) return null;
  return {
    name: readString(entry, "name"),
    workHighlights: readNumber(entry, "work_highlights"),
    docPositions: readStringArray(entry, "doc_positions"),
    assignedTo: readString(entry, "assigned_to"),
    uncertain: entry.uncertain === true,
  };
}

function isPresent<T>(entry: T | null): entry is T {
  return entry !== null;
}

/**
 * Narrow a raw `parsedJson` into the Bar view-model. Returns `null` when there is nothing to read
 * at all — a null, a primitive, an array, or an object with neither a log nor Bar arrays.
 */
export function readBarView(parsedJson: unknown): BarView | null {
  if (!isRecord(parsedJson)) return null;

  const log = isRecord(parsedJson.log) ? parsedJson.log : null;
  const rawBarristers = parsedJson.barristers;
  const rawUnattributed = parsedJson.unattributed_highlights;

  const barristers = Array.isArray(rawBarristers)
    ? rawBarristers.map(readBarrister).filter(isPresent)
    : [];
  const unattributedHighlights = Array.isArray(rawUnattributed)
    ? rawUnattributed.map(readUnattributedHighlight).filter(isPresent)
    : null;

  const hasBar =
    Array.isArray(rawBarristers) ||
    Array.isArray(rawUnattributed) ||
    (log !== null && (typeof log.bar_template === "string" || typeof log.bar_gate === "string"));

  if (!hasBar && log === null) return null;

  return { hasBar, barristers, unattributedHighlights, log };
}

/**
 * The template identifier the parser recorded, raw, or `null` when it recorded none — which is
 * every non-Bar submission, since V1 writes no identifier for the solicitor templates. An empty
 * string counts as none: it would render as a blank cell rather than as an answer.
 */
export function readBarTemplate(parsedJson: unknown): string | null {
  if (!isRecord(parsedJson)) return null;
  const log = parsedJson.log;
  if (!isRecord(log)) return null;
  const template = log.bar_template;
  if (typeof template !== "string" || template === "") return null;
  return template;
}

/** One log counter as a number, or `null` when the key is absent (every non-Bar document). */
export function readLogNumber(log: Record<string, unknown> | null, key: string): number | null {
  if (!log) return null;
  return readOptionalNumber(log, key);
}

/**
 * The parser's own verdict, or `null` when nothing tripped (or the document is not a Bar one).
 *
 * A tier this module does not recognise also reads as `null`, never as an error: a parse stored
 * before the tiers were renamed still holds the old wording, and such a row has to degrade to no
 * badge rather than throw in the middle of a table row.
 */
export function readBarVerdict(log: Record<string, unknown> | null): BarReviewVerdict | null {
  if (!log || !isRecord(log.bar_review)) return null;
  const tier = log.bar_review.tier;
  if (!isBarReviewTier(tier)) return null;
  return { tier, reasons: toBarReviewReasons(readRecordOfNumbers(log.bar_review.reasons)) };
}

export type BarStructureEntry = {
  key: string;
  label: string;
  count: number;
};

export type BarStructure = {
  /** Denominators — how many boxes were walked. They read 1 on a pristine template, not 0. */
  measured: BarStructureEntry[];
  /** Everything else in the measurement: distance from the uk_bar_2027 skeleton, 0 is clean. */
  deviations: BarStructureEntry[];
  /** Which skeleton labels were missing, and over how many tables/boxes. */
  missingLabels: BarStructureEntry[];
};

// The two keys of bar_structure_measurement that count what was measured rather than what deviated.
const BAR_STRUCTURE_DENOMINATORS: Record<string, string> = {
  barrister_boxes_measured: "Nominee boxes measured",
  highlight_boxes_measured: "Highlight boxes measured",
};

// The other eighteen. An unmapped key falls back to its raw name, as the reason codes do, so a
// counter added on the PHP side still shows up instead of disappearing.
const BAR_STRUCTURE_DEVIATION_LABELS: Record<string, string> = {
  head_label_missing: "Head table: labels missing",
  head_label_order: "Head table: labels out of order",
  head_rows_unknown: "Head table: rows no label owns",
  barrister_label_missing: "Nominee box: labels missing",
  barrister_label_order: "Nominee box: labels out of order",
  barrister_label_shape: "Nominee box: label rows of the wrong width",
  barrister_label_duplicate: "Nominee box: labels repeated",
  barrister_value_rows_extra: "Nominee box: surplus value rows",
  barrister_value_rows_fewer: "Nominee box: value rows missing",
  barrister_rows_unknown: "Nominee box: rows above the first label",
  highlight_label_missing: "Highlight box: labels missing",
  highlight_label_order: "Highlight box: labels out of order",
  highlight_label_shape: "Highlight box: label rows of the wrong width",
  highlight_label_duplicate: "Highlight box: labels repeated",
  highlight_value_rows_extra: "Highlight box: surplus value rows",
  highlight_value_rows_fewer: "Highlight box: value rows missing",
  highlight_rows_unknown: "Highlight box: rows above the first label",
  title_numbering_gaps: "Gaps in the box title numbering",
};

// bar_structure_missing_labels keys are "<kind>:<skeleton label>"; only the kind needs wording.
const BAR_STRUCTURE_LABEL_KINDS: Record<string, string> = {
  head: "Head table",
  barrister: "Nominee box",
  highlight: "Highlight box",
};

function formatMissingLabelKey(key: string): string {
  const separator = key.indexOf(":");
  if (separator === -1) return key;
  const kind = BAR_STRUCTURE_LABEL_KINDS[key.slice(0, separator)];
  if (!kind) return key;
  return `${kind}: ${key.slice(separator + 1)}`;
}

/**
 * The structure measurement, split so the two denominators are never read as deviations.
 * `null` when the parser recorded neither half — true for every Bar template except uk_bar_2027.
 */
export function readBarStructure(log: Record<string, unknown> | null): BarStructure | null {
  if (!log) return null;
  const measurement = readRecordOfNumbers(log.bar_structure_measurement);
  const missing = readRecordOfNumbers(log.bar_structure_missing_labels);
  if (measurement === null && missing === null) return null;

  const measured: BarStructureEntry[] = [];
  const deviations: BarStructureEntry[] = [];
  for (const [key, count] of Object.entries(measurement ?? {})) {
    const denominatorLabel = BAR_STRUCTURE_DENOMINATORS[key];
    if (denominatorLabel) {
      measured.push({ key, label: denominatorLabel, count });
    } else {
      deviations.push({ key, label: BAR_STRUCTURE_DEVIATION_LABELS[key] ?? key, count });
    }
  }

  const missingLabels = Object.entries(missing ?? {}).map(([key, count]) => ({
    key,
    label: formatMissingLabelKey(key),
    count,
  }));

  return { measured, deviations, missingLabels };
}
